import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useParams, useHistory } from "react-router-dom";
import { Button, Spinner, Badge } from "reactstrap";
import { strategyLabel } from "../../constants/strategies";

import StockPriceChart from "../../components/backtest/StockPriceChart";
import PnLChart from "../../components/backtest/PnLChart";
import LadderView from "../../components/backtest/LadderView";
import PositionsPanel from "../../components/backtest/PositionsPanel";
import StrategyInfoPanel from "../../components/backtest/StrategyInfoPanel";

// ── Action metadata ───────────────────────────────────────────────────────────

const ACTION_LABELS = {
  SELL_INITIAL_CALL:         "Sell Call Hedge",
  SELL_INITIAL_CSP:          "Sell Cash-Secured Put",
  ROLL_DOWN_CALL:            "Roll Down Call",
  ROLL_OUT_CALL:             "Roll Out Call",
  ROLLUP_CALL:               "Roll Up Call",
  ROLLUP_CSP:                "Roll Up CSP",
  BUY_STOCK:                 "Buy Stock (Ladder)",
  SQUARE_OFF_ALL_OPTIONS:    "Square Off All Options",
  SQUARE_OFF_CALLS:          "Square Off Calls",
  SQUARE_OFF_CALL_AND_STOCK: "Square Off Call + Stock",
  SQUARE_OFF_STOCKS:         "Square Off Stock",
  SITUATION_BRIEFING:        "Situation Briefing",
};

const ACTION_DESC = {
  SELL_INITIAL_CALL:         "Sell the initial call hedge at the derived strike to start collecting premium.",
  SELL_INITIAL_CSP:          "Sell a cash-secured put below the stock price for additional income.",
  ROLL_DOWN_CALL:            "Roll the short call to a lower strike — stock has dropped, the current call is now deep ITM.",
  ROLL_OUT_CALL:             "Roll the call to the next expiry — DTE is low and premium has decayed.",
  ROLLUP_CALL:               "Close the deep-ITM short call and sell a higher-strike OTM call. Captures more extrinsic value.",
  ROLLUP_CSP:                "Roll the short put to a higher strike, closer to LTP, for more premium.",
  BUY_STOCK:                 "Buy the next tranche of shares per the pyramid ladder.",
  SQUARE_OFF_ALL_OPTIONS:    "Close all open option positions for this symbol.",
  SQUARE_OFF_CALLS:          "Close all open call positions.",
  SQUARE_OFF_CALL_AND_STOCK: "Close the short call and sell all stock holdings to lock in net profit.",
  SQUARE_OFF_STOCKS:         "Sell the entire stock position.",
  SITUATION_BRIEFING:        "Price band breached. System evaluated rebalance options.",
};

const ACTION_COLORS = {
  SELL_INITIAL_CALL:         { bg: "#e8f5e9", border: "#4caf50", text: "#1b5e20", icon: "📞" },
  SELL_INITIAL_CSP:          { bg: "#e3f2fd", border: "#2196f3", text: "#0d47a1", icon: "📤" },
  ROLL_DOWN_CALL:            { bg: "#fff8e1", border: "#ff9800", text: "#e65100", icon: "↓" },
  ROLL_OUT_CALL:             { bg: "#fff8e1", border: "#ff9800", text: "#e65100", icon: "→" },
  ROLLUP_CALL:               { bg: "#fce4ec", border: "#e91e63", text: "#880e4f", icon: "↑" },
  ROLLUP_CSP:                { bg: "#fce4ec", border: "#e91e63", text: "#880e4f", icon: "↑" },
  BUY_STOCK:                 { bg: "#e8eaf6", border: "#3f51b5", text: "#1a237e", icon: "＋" },
  SQUARE_OFF_ALL_OPTIONS:    { bg: "#fde2e2", border: "#dc3545", text: "#721c24", icon: "✕" },
  SQUARE_OFF_CALLS:          { bg: "#fde2e2", border: "#dc3545", text: "#721c24", icon: "✕" },
  SQUARE_OFF_CALL_AND_STOCK: { bg: "#fde2e2", border: "#dc3545", text: "#721c24", icon: "✕" },
  SQUARE_OFF_STOCKS:         { bg: "#fde2e2", border: "#dc3545", text: "#721c24", icon: "✕" },
  SITUATION_BRIEFING:        { bg: "#f3e5f5", border: "#9c27b0", text: "#4a148c", icon: "ℹ" },
};

function getActionStyle(type) {
  return ACTION_COLORS[type] || { bg: "#f8f9fa", border: "#dee2e6", text: "#495057", icon: "⚙" };
}

function dollar(n) {
  const abs = `$${Math.round(Math.abs(n)).toLocaleString()}`;
  return n < 0 ? `−${abs}` : n > 0 ? `+${abs}` : abs;
}

const META_SKIP = new Set(["type", "side", "ladder", "strategy_name", "action_queue_id"]);

function fmtMetaVal(k, v) {
  if (v == null) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  const lk = k.toLowerCase();
  if (lk.includes("price") || lk.includes("ltp") || lk.includes("strike") || lk.includes("avg"))
    return `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (lk.includes("pct") || lk.includes("percent"))
    return `${Number(v).toFixed(2)}%`;
  if (lk.includes("delta"))
    return Number(v).toFixed(3);
  if (typeof v === "number")
    return Number.isInteger(v) ? v.toString() : Number(v).toFixed(2);
  return String(v);
}

// ── Pending suggestion card ───────────────────────────────────────────────────

function SuggestionCard({ action, onApprove, onSkip, disabled }) {
  const st = getActionStyle(action.action_type);
  const meta = action.metadata || {};

  const metaItems = Object.entries(meta)
    .filter(([k]) => !k.startsWith("_"))
    .slice(0, 5)
    .map(([k, v]) => ({ key: k, val: typeof v === "number" ? (Number.isInteger(v) ? v : v.toFixed(2)) : String(v) }));

  return (
    <div style={{
      border: `1.5px solid ${st.border}`,
      borderRadius: 8,
      background: st.bg,
      padding: "12px 14px",
      marginBottom: 10,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span style={{ fontSize: 18, lineHeight: 1.3 }}>{st.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: st.text }}>
            {ACTION_LABELS[action.action_type] || action.action_type}
          </div>
          <div style={{ fontSize: 12, color: "#555", marginTop: 2, lineHeight: 1.4 }}>
            {ACTION_DESC[action.action_type] || ""}
          </div>
          {metaItems.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
              {metaItems.map(({ key, val }) => (
                <span key={key} style={metaPill}>
                  <span style={{ color: "#888", fontWeight: 400 }}>{key.replace(/_/g, " ")}: </span>
                  <strong>{val}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button
          onClick={() => onApprove(action.id)}
          disabled={disabled}
          style={{ ...btnBase, background: "#28a745", color: "#fff", borderColor: "#28a745", opacity: disabled ? 0.6 : 1 }}
        >
          ✓ Approve
        </button>
        <button
          onClick={() => onSkip(action.id)}
          disabled={disabled}
          style={{ ...btnBase, background: "#fff", color: "#6c757d", borderColor: "#dee2e6", opacity: disabled ? 0.6 : 1 }}
        >
          ✗ Skip
        </button>
      </div>
    </div>
  );
}

// ── P&L Summary bar ───────────────────────────────────────────────────────────

function PnLSummary({ frame }) {
  if (!frame) return null;
  const pnl = frame.pnl || {};
  const total = pnl.cumulative || 0;

  const items = [
    { label: "Opts Realized", value: pnl.realized_options  || 0, color: "#6f42c1", note: "booked" },
    { label: "Stock",         value: pnl.unrealised_stock   || 0, color: "#3b82f6", note: "MtM" },
    { label: "Open Opts",     value: pnl.unrealised_options || 0, color: "#fd7e14", note: "MtM" },
  ];
  const premium = pnl.premium_collected || 0;

  return (
    <div>
      <div style={pnlBar}>
        {items.map(item => (
          <div key={item.label} style={pnlItem}>
            <div style={{ fontSize: 10, color: "#888", marginBottom: 2 }}>
              {item.label} <span style={{ fontSize: 9, color: "#ccc" }}>{item.note}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: item.value >= 0 ? item.color : "#dc3545" }}>
              {item.value >= 0 ? "+" : "−"}${Math.round(Math.abs(item.value)).toLocaleString()}
            </div>
          </div>
        ))}
        <div style={{ ...pnlItem, borderLeft: "1px solid #e9ecef", paddingLeft: 12 }}>
          <div style={{ fontSize: 10, color: "#888", marginBottom: 2 }}>Total P&L</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: total >= 0 ? "#28a745" : "#dc3545" }}>
            {total >= 0 ? "+" : "−"}${Math.round(Math.abs(total)).toLocaleString()}
          </div>
        </div>
      </div>
      {premium !== 0 && (
        <div style={{ fontSize: 10, color: "#aaa", marginTop: 5 }}>
          ℹ {premium >= 0 ? "+" : "−"}${Math.round(Math.abs(premium)).toLocaleString()} gross option income (netted into Opts Realized)
        </div>
      )}
    </div>
  );
}

// ── Adjustments log table ─────────────────────────────────────────────────────

function IAdjLogRow({ r, expanded, onToggle }) {
  const [hovered, setHovered] = useState(false);
  const st = getActionStyle(r.action_type);

  const allPairs = Object.entries(r.metadata)
    .filter(([k, v]) => !META_SKIP.has(k) && !k.startsWith("_") && typeof v !== "object" && v != null && v !== "");

  const shortDetails = allPairs
    .slice(0, 3)
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${fmtMetaVal(k, v)}`)
    .join("  ·  ");

  return (
    <React.Fragment>
      <tr
        onClick={onToggle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          borderBottom: expanded ? "none" : "1px solid #f0f0f0",
          cursor: "pointer",
          background: expanded ? "#f0f4ff" : hovered ? "#f9f9fb" : "transparent",
          transition: "background 0.1s",
        }}
      >
        <td style={td}>{r.date}</td>
        <td style={td}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: st.text, paddingLeft: 7, borderLeft: `3px solid ${st.border}` }}>
            {st.icon} {ACTION_LABELS[r.action_type] || r.action_type}
          </span>
        </td>
        <td style={{ ...td, color: "#888", fontSize: 11, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {shortDetails || "—"}
        </td>
        <td style={{ ...td, textAlign: "right" }}>
          <span style={{ fontWeight: 600, color: r.pnl >= 0 ? "#28a745" : "#dc3545" }}>
            {dollar(r.pnl)}
          </span>
          <span style={{ marginLeft: 10, color: "#bbb", fontSize: 11 }}>{expanded ? "▲" : "▼"}</span>
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: "#f0f4ff", borderBottom: "2px solid #dde6ff" }}>
          <td colSpan={4} style={{ padding: "12px 20px 14px 28px" }}>
            {ACTION_DESC[r.action_type] && (
              <div style={{ fontSize: 11, color: "#555", fontStyle: "italic", lineHeight: 1.55, marginBottom: allPairs.length ? 12 : 0 }}>
                {ACTION_DESC[r.action_type]}
              </div>
            )}
            {allPairs.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 32px" }}>
                {allPairs.map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>
                      {k.replace(/_/g, " ")}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#2c3e50", marginTop: 2, whiteSpace: "nowrap" }}>
                      {fmtMetaVal(k, v)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

function AdjustmentsLog({ frames }) {
  const [expanded, setExpanded] = useState(null);

  const rows = frames.flatMap(f =>
    (f.events || [])
      .filter(e => e.action_type)
      .map(e => ({
        date: f.date,
        action_type: e.action_type,
        metadata: e.metadata || {},
        pnl: f.pnl?.cumulative || 0,
      }))
  );

  if (rows.length === 0) {
    return (
      <div style={{ color: "#bbb", fontSize: 12, textAlign: "center", padding: "20px 0" }}>
        No adjustments yet — approve a suggestion to see it here.
      </div>
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
            <th style={th}>Date</th>
            <th style={th}>Action</th>
            <th style={th}>Details</th>
            <th style={{ ...th, textAlign: "right" }}>Cumulative P&L</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <IAdjLogRow
              key={i}
              r={r}
              expanded={expanded === i}
              onToggle={() => setExpanded(expanded === i ? null : i)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function InteractiveBacktest() {
  const { sessionId } = useParams();
  const history = useHistory();

  const [session, setSession]             = useState(null);
  const [frames, setFrames]               = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [stepping, setStepping]           = useState(false);
  const [actioning, setActioning]         = useState(null);  // action id being processed
  const [done, setDone]                   = useState(false);
  const [err, setErr]                     = useState(null);
  const [ending, setEnding]               = useState(false);

  const bottomRef = useRef(null);

  const currentFrame = frames.length > 0 ? frames[frames.length - 1] : null;
  const currentIndex = frames.length - 1;

  // Load or resume session on mount
  useEffect(() => {
    if (!sessionId) return;
    axios.get(`/backtest/interactive/${sessionId}`)
      .then(r => {
        const data = r.data;
        setSession({
          id:                 data.session_id,
          symbol:             data.symbol,
          strategy_type:      data.strategy_type,
          start_date:         data.start_date,
          end_date:           data.end_date,
          expiry:             data.expiry,
          total_trading_days: data.total_trading_days,
        });
        setFrames(data.frames || []);
        setPendingActions(data.pending_actions || []);
        if (data.status === "COMPLETED") setDone(true);
      })
      .catch(e => setErr(e.response?.data?.error || "Failed to load session."));
  }, [sessionId]);

  const handleStep = useCallback(async (unit) => {
    if (!session || stepping || done) return;
    setStepping(true);
    setErr(null);
    try {
      const r = await axios.post(`/backtest/interactive/${sessionId}/step`, { unit });
      const data = r.data;

      if (data.done) {
        setDone(true);
        setStepping(false);
        return;
      }

      setFrames(prev => {
        // Replace or append: if frame for this day_index already exists, update it
        const existing = prev.findIndex(f => f.day_index === data.frame.day_index);
        if (existing >= 0) {
          const next = [...prev];
          next[existing] = data.frame;
          return next;
        }
        return [...prev, data.frame];
      });
      setPendingActions(data.pending_actions || []);

      // Scroll to suggestions if any fired
      if (data.pending_actions?.length > 0 && bottomRef.current) {
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
      }
    } catch (e) {
      setErr(e.response?.data?.error || "Step failed. Please try again.");
    }
    setStepping(false);
  }, [session, sessionId, stepping, done]);

  const handleApprove = useCallback(async (actionId) => {
    setActioning(actionId);
    setErr(null);
    try {
      const r = await axios.post(`/backtest/interactive/${sessionId}/approve/${actionId}`);
      const data = r.data;
      // Update the frame that was modified
      setFrames(prev => {
        const existing = prev.findIndex(f => f.day_index === data.day_index);
        if (existing >= 0) {
          const next = [...prev];
          next[existing] = data.frame;
          return next;
        }
        return prev;
      });
      setPendingActions(data.pending_actions || []);
    } catch (e) {
      setErr(e.response?.data?.error || "Approve failed.");
    }
    setActioning(null);
  }, [sessionId]);

  const handleSkip = useCallback(async (actionId) => {
    setActioning(actionId);
    setErr(null);
    try {
      const r = await axios.post(`/backtest/interactive/${sessionId}/skip/${actionId}`);
      setPendingActions(r.data.pending_actions || []);
    } catch (e) {
      setErr(e.response?.data?.error || "Skip failed.");
    }
    setActioning(null);
  }, [sessionId]);

  const handleEnd = useCallback(async () => {
    if (!window.confirm("End this session? The virtual positions will be cleaned up.")) return;
    setEnding(true);
    try {
      await axios.delete(`/backtest/interactive/${sessionId}`);
      history.push("/backtest");
    } catch (e) {
      setErr("Failed to end session.");
      setEnding(false);
    }
  }, [sessionId, history]);

  if (!session) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        {err
          ? <div style={{ color: "#dc3545" }}>{err}</div>
          : <><Spinner /> <span style={{ marginLeft: 10, color: "#888" }}>Loading session…</span></>
        }
      </div>
    );
  }

  const isPH = ["PYRAMID_HEDGING", "FUTURES_PYRAMID_HEDGING"].includes(session.strategy_type);
  const daysDone = currentFrame ? currentFrame.day_index + 1 : 0;
  const totalDays = session.total_trading_days;

  // Days to expiry from current frame date
  const dte = session.expiry && currentFrame?.date
    ? Math.round((new Date(session.expiry) - new Date(currentFrame.date)) / 86400000)
    : null;

  // Stock % move from first frame to current
  const firstFrame = frames[0];
  const stockMove = firstFrame?.stock_ltp && currentFrame?.stock_ltp && frames.length > 1
    ? ((currentFrame.stock_ltp - firstFrame.stock_ltp) / firstFrame.stock_ltp) * 100
    : null;

  // Adjustments count and final P&L for summary card
  const allAdjustments = frames.flatMap(f => (f.events || []).filter(e => e.action_type));
  const lastPnL = frames.length > 0 ? (frames[frames.length - 1].pnl || {}) : {};

  return (
    <div style={{ padding: "0 0 32px 0" }}>

      {/* ── Header ── */}
      <div style={headerWrap}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: "#2c3e50" }}>
              {session.symbol}
            </span>
            <span style={stratBadge}>{strategyLabel(session.strategy_type)}</span>
            {done && <span style={doneBadge}>Session Complete</span>}
            {dte !== null && (
              <span style={{ fontSize: 11, background: dte <= 7 ? "#fff3cd" : "#f8f9fa", color: dte <= 7 ? "#856404" : "#6c757d", padding: "2px 8px", borderRadius: 12, border: "1px solid #e9ecef" }}>
                DTE {dte}
              </span>
            )}
            {stockMove !== null && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 12,
                background: stockMove >= 0 ? "#e8f5e9" : "#fde2e2",
                color: stockMove >= 0 ? "#1b5e20" : "#721c24",
              }}>
                {stockMove >= 0 ? "+" : ""}{stockMove.toFixed(1)}%
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 3 }}>
            {session.start_date} → {session.end_date}
            {session.expiry ? ` · Expiry ${session.expiry}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {totalDays > 0 && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#2c3e50" }}>
                Day {daysDone} of {totalDays}
              </div>
              {currentFrame && (
                <div style={{ fontSize: 11, color: "#888" }}>{currentFrame.date}</div>
              )}
            </div>
          )}
          <button
            onClick={handleEnd}
            disabled={ending}
            style={{ ...btnBase, background: "#fff", color: "#6c757d", borderColor: "#dee2e6", fontSize: 11, padding: "4px 10px" }}
          >
            {ending ? <Spinner size="sm" /> : "End Session"}
          </button>
        </div>
      </div>

      {/* ── Progress bar ── */}
      {totalDays > 0 && (
        <div style={{ height: 4, background: "#e9ecef", margin: "0 0 16px" }}>
          <div style={{
            height: 4,
            background: done ? "#28a745" : "#3b82f6",
            width: `${Math.round((daysDone / totalDays) * 100)}%`,
            transition: "width 0.4s",
          }} />
        </div>
      )}

      {err && (
        <div style={{ background: "#fde2e2", color: "#721c24", padding: "8px 14px", borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          {err}
        </div>
      )}

      {/* ── Main grid: left panel + right charts ── */}
      <div style={mainGrid}>

        {/* ── Left: Positions + P&L summary ── */}
        <div style={leftPanel}>
          <div style={panelCard}>
            <div style={panelTitle}>Positions</div>
            <PositionsPanel frame={currentFrame} />
          </div>

          <div style={{ ...panelCard, marginTop: 12 }}>
            <div style={panelTitle}>P&L</div>
            <PnLSummary frame={currentFrame} />
          </div>

          {isPH ? (
            <div style={{ ...panelCard, marginTop: 12 }}>
              <div style={panelTitle}>Ladder</div>
              <LadderView frame={currentFrame} />
            </div>
          ) : (
            <div style={{ ...panelCard, marginTop: 12 }}>
              <StrategyInfoPanel frame={currentFrame} strategyType={session.strategy_type} />
            </div>
          )}
        </div>

        {/* ── Right: Charts + suggestions ── */}
        <div style={rightPanel}>
          {/* Stock price chart */}
          <div style={panelCard}>
            <div style={panelTitle}>
              {session.symbol} Price
              {currentFrame && (
                <span style={{ fontWeight: 400, color: "#888", marginLeft: 8 }}>
                  ${currentFrame.stock_ltp?.toFixed(2)}
                </span>
              )}
            </div>
            {frames.length > 0
              ? <StockPriceChart frames={frames} currentIndex={currentIndex} />
              : <div style={emptyChart}>Step forward to see the price chart.</div>
            }
          </div>

          {/* P&L chart */}
          {frames.length > 1 && (
            <div style={{ ...panelCard, marginTop: 12 }}>
              <div style={panelTitle}>Cumulative P&L</div>
              <PnLChart frames={frames} currentIndex={currentIndex} />
            </div>
          )}

          {/* Pending suggestions */}
          {pendingActions.length > 0 && (
            <div style={{ ...panelCard, marginTop: 12, border: "1.5px solid #fd7e14" }}>
              <div style={{ ...panelTitle, color: "#e65100" }}>
                ★ Suggestions — {currentFrame?.date}
                <span style={{ fontWeight: 400, fontSize: 11, color: "#888", marginLeft: 8 }}>
                  Approve to execute, or Skip to ignore
                </span>
              </div>
              {pendingActions.map(action => (
                <SuggestionCard
                  key={action.id}
                  action={action}
                  onApprove={handleApprove}
                  onSkip={handleSkip}
                  disabled={actioning !== null}
                />
              ))}
            </div>
          )}

          {/* "No suggestions" quiet state */}
          {frames.length > 0 && pendingActions.length === 0 && !done && (
            <div style={{ ...panelCard, marginTop: 12, background: "#f8fffe", border: "1px solid #e0f0ee" }}>
              <div style={{ fontSize: 12, color: "#5e9e8a", textAlign: "center", padding: "8px 0" }}>
                No suggestions for this period — step forward to continue.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Step controls ── */}
      <div style={controlsBar} ref={bottomRef}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button
            color="primary"
            onClick={() => handleStep("hour")}
            disabled={stepping || done || pendingActions.length > 0}
            style={{ minWidth: 110 }}
          >
            {stepping ? <Spinner size="sm" /> : "⏭ +1 Hour"}
          </Button>
          <Button
            color="primary"
            onClick={() => handleStep("day")}
            disabled={stepping || done || pendingActions.length > 0}
            style={{ minWidth: 110 }}
          >
            {stepping ? <Spinner size="sm" /> : "⏭⏭ +1 Day"}
          </Button>
          {pendingActions.length > 0 && (
            <span style={{ fontSize: 12, color: "#e65100", fontStyle: "italic" }}>
              Resolve suggestions before stepping forward
            </span>
          )}
          {done && (
            <span style={{ fontSize: 12, color: "#28a745", fontWeight: 600 }}>
              ✓ End of backtest period reached
            </span>
          )}
        </div>
        {currentFrame && (
          <div style={{ fontSize: 12, color: "#888" }}>
            Virtual date: <strong style={{ color: "#2c3e50" }}>{currentFrame.date}</strong>
            {currentFrame.stock_ltp ? ` · ${session.symbol} $${currentFrame.stock_ltp.toFixed(2)}` : ""}
          </div>
        )}
      </div>

      {/* ── Session summary (when done) ── */}
      {done && (
        <div style={summaryCard}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#2c3e50", marginBottom: 12 }}>
            Session Complete — {daysDone} Days Simulated
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {[
              { label: "Adjustments",            value: allAdjustments.length, fmt: n => `${n}` },
              { label: "Opts Realized (booked)",  value: lastPnL.realized_options  || 0, fmt: dollar },
              { label: "Stock MtM",              value: lastPnL.unrealised_stock   || 0, fmt: dollar },
              { label: "Open Options MtM",       value: lastPnL.unrealised_options || 0, fmt: dollar },
              { label: "Total P&L",              value: lastPnL.cumulative         || 0, fmt: dollar, large: true },
            ].map(it => (
              <div key={it.label}>
                <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.4px" }}>{it.label}</div>
                <div style={{ fontSize: it.large ? 18 : 14, fontWeight: it.large ? 700 : 600, marginTop: 2, color: typeof it.value === "number" && it.value < 0 ? "#dc3545" : typeof it.value === "number" && it.value > 0 && it.label !== "Adjustments" ? "#28a745" : "#2c3e50" }}>
                  {it.fmt(it.value)}
                </div>
              </div>
            ))}
          </div>
          {(lastPnL.premium_collected || 0) !== 0 && (
            <div style={{ fontSize: 11, color: "#999", marginTop: 10, borderTop: "1px solid #e0e8e0", paddingTop: 8 }}>
              ℹ Gross option income collected: {dollar(lastPnL.premium_collected || 0)} — roll/close costs are netted into "Opts Realized" above.
            </div>
          )}
          <button
            onClick={() => history.push("/backtest")}
            style={{ marginTop: 14, padding: "6px 14px", fontSize: 12, cursor: "pointer", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600 }}
          >
            ← Start New Session
          </button>
        </div>
      )}

      {/* ── Adjustments log ── */}
      <div style={{ ...panelCard, margin: "16px 0 0" }}>
        <div style={{ ...panelTitle, marginBottom: 12 }}>
          Adjustments Log
          <span style={{ fontWeight: 400, fontSize: 11, color: "#888", marginLeft: 8 }}>
            All executed trades across this session
          </span>
        </div>
        <AdjustmentsLog frames={frames} />
      </div>

    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const headerWrap = {
  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  padding: "0 0 14px", borderBottom: "1px solid #e9ecef", marginBottom: 16,
};
const stratBadge = {
  fontSize: 11, fontWeight: 700, background: "#e8f5e9", color: "#1b5e20",
  border: "1px solid #81c784", padding: "2px 8px", borderRadius: 20,
};
const doneBadge = {
  fontSize: 11, fontWeight: 700, background: "#d4edda", color: "#155724",
  border: "1px solid #c3e6cb", padding: "2px 8px", borderRadius: 20,
};
const summaryCard = {
  background: "linear-gradient(135deg, #e8f5e9 0%, #f0f9ff 100%)",
  border: "1.5px solid #81c784",
  borderRadius: 10, padding: "16px 20px", marginTop: 16,
};
const mainGrid = {
  display: "grid",
  gridTemplateColumns: "240px 1fr",
  gap: 14,
  alignItems: "start",
};
const leftPanel  = { display: "flex", flexDirection: "column" };
const rightPanel = { display: "flex", flexDirection: "column" };
const panelCard  = {
  background: "#fff", border: "1px solid #e9ecef", borderRadius: 8,
  padding: "12px 14px",
};
const panelTitle = {
  fontSize: 11, fontWeight: 700, color: "#6c757d",
  textTransform: "uppercase", letterSpacing: "0.5px",
  marginBottom: 10,
};
const controlsBar = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  background: "#fff", border: "1px solid #e9ecef", borderRadius: 8,
  padding: "12px 16px", marginTop: 16,
};
const pnlBar  = { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" };
const pnlItem = { display: "flex", flexDirection: "column" };
const th = { padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#6c757d", fontSize: 11, textTransform: "uppercase" };
const td = { padding: "7px 10px", color: "#333", verticalAlign: "middle" };
const emptyChart  = { height: 120, display: "flex", alignItems: "center", justifyContent: "center", color: "#bbb", fontSize: 12 };
const metaPill    = { fontSize: 11, background: "rgba(0,0,0,0.06)", padding: "2px 8px", borderRadius: 12, color: "#444" };
const btnBase     = {
  padding: "5px 14px", fontSize: 12, fontWeight: 600, borderRadius: 6,
  border: "1px solid", cursor: "pointer", transition: "opacity 0.15s",
  display: "inline-flex", alignItems: "center", gap: 5,
};
